import React, { useCallback, useContext, useEffect } from 'react';
import {
  arrayOf,
  bool,
  func,
  number,
  string,
} from 'prop-types';

import {
  Button,
  FormController,
  FormLabel,
  IconButton,
  Radio, RadioGroup, SelectionControlLabel,
  Typography
} from '@one-thd/sui-atomic-components';
import {
  Calculator as CalculatorIcon,
  TrashCan
} from '@one-thd/sui-icons';

import { CalculatorContext } from './subcomponents/CalculatorProvider';
import { hasError } from '../helpers/errorHandlingUtil';
import { CalculatorInputElement } from './subcomponents/CalculatorInputElement';

const MIN_AREA_NUMBER = 1;

const Calculator = ({
  descriptionText,
  dimensionNames,
  hideActions,
  hideAreaText,
  hideDescription,
  hideHeader,
  maxAreaNumber,
  showNameInputs,
  selectedArea,
  renderMulchOptions
}) => {
  const {
    addElement,
    calculate,
    calculateTotal,
    calculatorType,
    lineItemName,
    removeInputElement,
    setCalculate
  } = useContext(CalculatorContext);

  const { areas, areaErrors, calcByLengthWidth, calcByArea } = calculate;

  const handleToggleText = useCallback(() => {
    const adjustedAreas = areas.map((area) => {
      const { length, squareFootage, width } = area;
      let sqFtValue;
      if (squareFootage.changed) {
        sqFtValue = squareFootage.value;
      } else {
        sqFtValue = length && width ? (length * width).toString() : squareFootage.value;
      }
      return {
        ...area,
        length: squareFootage.changed ? '' : length,
        width: squareFootage.changed ? '' : width,
        squareFootage: {
          value: sqFtValue,
          changed: false
        }
      };
    });
    setCalculate({
      ...calculate,
      calcByArea: !calcByArea,
      calcByLengthWidth: !calcByLengthWidth,
      areas: adjustedAreas
    });
  }, [areas, calcByArea]);

  const prompt = 'How much will you need?';
  const description = 'Please note: calculations are estimates only';

  // Get dimension names after determining calculator Model
  const dimensions = (dimensionNames && dimensionNames.length !== 0)
    ? dimensionNames.reduce((names, currentDimension, i) => {
      const dimensionList = names;
      dimensionList['dimension' + (i + 1)] = currentDimension;
      return dimensionList;
    }, {})
    : { dimension1: 'Length', dimension2: 'Width' };

  const depthToggleLabel = calculatorType === 'Volume' ? ' x Depth' : '';
  const lwToggleLabel = `${dimensions.dimension1} x ${dimensions.dimension2}${depthToggleLabel}`;
  const sqFtToggleLabel = `Square Footage${depthToggleLabel}`;

  const filteredAreas = areas.filter((area) => !selectedArea || (area.key === selectedArea));

  return (
    <div className="sui-bg-transparent sui-mt-3 sui-mb-5">
      {!hideHeader && (
        <div className="sui-flex">
          <CalculatorIcon color="brand" />
          <span className="sui-ml-2">
            <Typography variant="body-lg" weight="bold">{prompt}</Typography>
          </span>
        </div>
      )}
      {!hideDescription && (
        <Typography>
          {descriptionText || description}
        </Typography>
      )}
      {renderMulchOptions && renderMulchOptions()}
      <div className="sui-flex sui-mt-3 sui-mb-3">
        <div className="sui-pr-5">
          <FormController>
            <span className="sui-pt-1"><FormLabel>Calculate by:</FormLabel></span>
            <RadioGroup>
              <SelectionControlLabel value={lwToggleLabel} label={lwToggleLabel}>
                <Radio
                  onChange={handleToggleText}
                  checked={calculate.calcByLengthWidth}
                />
              </SelectionControlLabel>
              <SelectionControlLabel value={sqFtToggleLabel} label={sqFtToggleLabel}>
                <Radio
                  onChange={handleToggleText}
                  checked={calculate.calcByArea}
                />
              </SelectionControlLabel>
            </RadioGroup>
          </FormController>
        </div>
      </div>
      <div id="calc-input-sections" className="sui-overflow-auto sui-max-h-80">
        {filteredAreas.map((element, elementIndex) => (
          <div key={elementIndex} className="sui-my-2">
            <div className="sui-flex sui-items-center">
              {!hideAreaText && (
                <div className="sui-mr-4">
                  <Typography weight="bold">{element.name}</Typography>
                </div>
              )}
              {!hideActions && (areas.length !== 1 || calculate.isPresentInLocalStorage) && (
                <IconButton icon={TrashCan} color="brand" onClick={() => removeInputElement(elementIndex)} />
              )}
            </div>
            <div className="sui-flex sui-flex-col sm:sui-flex-row sui-mt-1">
              {showNameInputs && (
                <CalculatorInputElement
                  index={elementIndex}
                  propName="Description"
                  propValue={element.name}
                  isError={areaErrors[elementIndex].name}
                />
              )}
              {calculate.calcByLengthWidth && (
                <>
                  <CalculatorInputElement
                    index={Number(element.key)}
                    labelName={dimensions.dimension1}
                    propName="length"
                    propValue={element.length}
                    isError={areaErrors[Number(element.key)].length}
                    endAdornment="ft"
                  />
                  <CalculatorInputElement
                    index={Number(element.key)}
                    labelName={dimensions.dimension2}
                    propName="width"
                    propValue={element.width}
                    isError={areaErrors[Number(element.key)].width}
                    endAdornment="ft"
                  />
                </>
              )}
              {calculate.calcByArea
                && (
                  <CalculatorInputElement
                    index={elementIndex}
                    propName="Square Footage"
                    propValue={element.squareFootage.value}
                    isError={areaErrors[elementIndex].squareFootage}
                    endAdornment="sq. ft"
                  />
                )}
              {calculatorType === 'Volume'
                && (
                  <CalculatorInputElement
                    index={elementIndex}
                    propName="Depth"
                    propValue={element.depth}
                    isError={areaErrors[elementIndex].depth}
                    endAdornment="in"
                  />
                )}
            </div>
          </div>
        ))}
      </div>
      { !hideActions && (
        <>
          { areas.length < maxAreaNumber && (
            <Button variant="text" onClick={addElement} data-testid="add-area">{`+ Add ${lineItemName}`}</Button>
          )}
          <div className="sui-mt-4">
            <Button
              variant="primary"
              disabled={hasError(areaErrors, calcByArea)}
              onClick={() => calculateTotal({ isClick: true })}
            >
              Calculate
            </Button>
          </div>
        </>
      )}
      {renderMulchOptions && (
        <div className="sui-mt-5">
          <Typography variant="body-xs" color="subtle">
            {description}
          </Typography>
        </div>
      )}
    </div>
  );
};

Calculator.displayName = 'Calculator';

Calculator.propTypes = {
  descriptionText: string,
  dimensionNames: arrayOf(string),
  hideActions: bool,
  hideAreaText: bool,
  hideDescription: bool,
  hideHeader: bool,
  maxAreaNumber: number,
  renderMulchOptions: func,
  selectedArea: string,
  showNameInputs: bool,
};

Calculator.defaultProps = {
  descriptionText: '',
  dimensionNames: ['Width', 'Height'],
  hideActions: false,
  hideAreaText: false,
  hideDescription: false,
  hideHeader: false,
  maxAreaNumber: 3,
  renderMulchOptions: null,
  selectedArea: '',
  showNameInputs: false
};

export { Calculator };
